import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/UI/Button';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import { Mail, CheckCircle, Loader2, LogOut } from 'lucide-react';
import logo from '../assets/Autograph-logo-01-01.svg';

const VerifyEmailRequiredPage = () => {
  const { user, resendVerificationEmail, logout, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Get the intended destination from location state, or default to dashboard
  const from = location.state?.from?.pathname || '/dashboard';

  // Redirect if user is already verified
  useEffect(() => {
    if (!loading && user?.emailVerified) {
      navigate(from, { replace: true });
    }
  }, [user, loading, navigate, from]);

  const handleResend = async () => {
    if (isResending || resendCooldown > 0) return;

    setIsResending(true);
    const result = await resendVerificationEmail();
    setIsResending(false);

    if (result.success) {
      setResendCooldown(60);
      const interval = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Show loading while checking auth state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // If user is verified, show nothing while redirecting
  if (user?.emailVerified) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ag-black-800 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <img
            src={logo}
            alt="Autograph Logo"
            className="h-16 w-auto"
          />
        </div>

        <h2 className="mt-6 text-center text-3xl font-bold text-ag-white-200">
          Verify Your Email
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          To access all features, please verify your email address
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-ag-black-600 py-8 px-4 shadow-3xl sm:rounded-lg sm:px-10">
          <div className="text-center">
            <p className="text-gray-400 mb-2">
              We sent a verification email to:
            </p>
            <p className="font-semibold text-gray-300 mb-6">
              {user?.email}
            </p>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-ag-black-800">
                Click the link in the email to verify your account. The link will expire in 24 hours.
              </p>
            </div>

            <div className="space-y-4">
              <Button
                onClick={handleResend}
                disabled={isResending || resendCooldown > 0}
                className="w-full"
                size="lg"
              >
                {isResending ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : resendCooldown > 0 ? (
                  `Resend in ${resendCooldown}s`
                ) : (
                  <>
                    <Mail className="h-5 w-5 mr-2" />
                    Resend Verification Email
                  </>
                )}
              </Button>

              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-300 hover:text-gray-500"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sign out and use a different account
              </button>
            </div>

            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <div className="ml-3 text-left">
                  <p className="text-sm text-gray-700">
                    <strong>Tip:</strong> Check your spam or junk folder if you don't see the email in your inbox.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailRequiredPage;
